import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDateInput from '../../../components/form/FormDateInput';
import FormSelect from '../../../components/form/FormSelect';
import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';

import ProductReportDetails from '../ProductReport/ProductReportDetails';
import {
  archiveOptionsGet,
  callConnectivityReportGet,
  downloadCallConnectivityReport,
  getListsByProcess,
  processOptionGetByFlag
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import {
  formatArchiveOptions,
  formatPayloadDate,
  getDateRangeFromLabel
} from '../../../utils/common';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';

// Raw disposition / hangup_cause value -> display label.
// Multiple raw values can map to the same label; their counts get merged.
// Naya mapping chahiye to bas yahan ek line add kar do.
const DISPOSITION_LABEL_MAP = {
  NORMAL_CLEARING: 'SUCCESS',
  AUTOORIGINATE: 'SUCCESS'
};

// Labels jo "Connected" table me dikhenge. Baaki sab "Not Connected" me.
const CONNECTED_LABELS = ['SUCCESS'];

// API ab `disposition` bhejta hai; `hangup_cause` fallback purane responses ke liye.
const getRawCause = (item) => item?.disposition ?? item?.hangup_cause ?? 'UNKNOWN';

const getLabel = (raw) => DISPOSITION_LABEL_MAP[raw] || raw;

// Raw API rows ko display rows me convert karta hai:
// - label remap
// - same label wale rows ka count merge
// - original raw values `rawCauses` me preserve (download ke liye)
const buildReportRows = (rows = []) => {
  const merged = (Array.isArray(rows) ? rows : []).reduce((acc, item) => {
    const raw = getRawCause(item);
    const label = getLabel(raw);
    const count = Number(item?.count) || 0;

    if (!acc[label]) {
      acc[label] = { label, count: 0, rawCauses: [] };
    }
    acc[label].count += count;
    if (!acc[label].rawCauses.includes(raw)) {
      acc[label].rawCauses.push(raw);
    }
    return acc;
  }, {});

  return Object.values(merged).sort((a, b) => b.count - a.count);
};

// Always offered, and always the default: with no list chosen the report covers every
// list, which is also what the payload sends.
const ALL_LIST_OPTION = { label: 'All', value: 'All' };

const sumCounts = (rows = []) => rows.reduce((acc, val) => acc + (Number(val?.count) || 0), 0);

const collectRawCauses = (rows = []) => rows.flatMap((val) => val?.rawCauses || []);

const CallConnectivityReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [processOptions, setProcessOptions] = useState([]);
  // Starts true because the first search waits for the archive options to resolve -
  // without this the empty state flashes before the loader appears.
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState({
    normalClearing: [],
    other: []
  });
  const [infoDetails, setInfoDetails] = useState(null);
  const [archiveOptions, setArchiveOptions] = useState([]);
  const [listOptions, setListOptions] = useState([ALL_LIST_OPTION]);
  const hasAutoSearchedRef = useRef(false);

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    processId: '',
    listId: 'All',
    archiveId: 0
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required')
  });

  // `range` lets the first automatic search use the month range as soon as it is
  // derived, without waiting for setFieldValue to land in `values`.
  const fetchReport = async (range) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(range?.start_date || values.start_date);
      const formattedEndDate = formatPayloadDate(range?.end_date || values.end_date);
      const payload = {
        ...values,
        processId: values?.processId || 'All',
        listId: values?.listId || 'All'
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;

      const response = await dispatch(callConnectivityReportGet(payload)).unwrap();
      if (response) {
        const allRows = buildReportRows(response?.data);

        setReportData({
          normalClearing: allRows.filter((row) => CONNECTED_LABELS.includes(row.label)),
          other: allRows.filter((row) => !CONNECTED_LABELS.includes(row.label))
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const onSubmit = () => fetchReport();

  // Runs the load-time search exactly once, whichever path resolves the range first.
  const runInitialSearch = (range) => {
    if (hasAutoSearchedRef.current) return;
    hasAutoSearchedRef.current = true;
    fetchReport(range);
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const getProcessOption = async () => {
    try {
      const response = await dispatch(
        processOptionGetByFlag({ flag_type: 'voicebroadcast' })
      ).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getListOption = async (id) => {
    try {
      const response = await dispatch(getListsByProcess({ process_id: id })).unwrap();
      const options =
        response?.data?.map((val) => ({ label: val.list_name, value: val.id })) || [];
      setListOptions([ALL_LIST_OPTION, ...options]);
    } catch (error) {
      setListOptions([ALL_LIST_OPTION]);
      console.log('error', error);
    }
  };

  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(archiveOptionsGet()).unwrap();
      if (response && response.data) {
        const options = formatArchiveOptions(response.data, timezone);
        setArchiveOptions(options);
      } else {
        setArchiveOptions([]);
        // No month options means no derived range, so fall back to the default dates.
        runInitialSearch();
      }
    } catch (error) {
      setArchiveOptions([]);
      runInitialSearch();
      console.log('error', error);
    }
  };

  useEffect(() => {
    getArchiveOptions();
    getProcessOption();
  }, []);

  // Lists belong to a process, so they can only be fetched once one is chosen. Reset the
  // selection back to All on every process change — a list id from the previous process
  // would otherwise stay in the payload and silently filter the report to nothing.
  useEffect(() => {
    if (values.processId) {
      getListOption(values.processId);
    } else {
      // No process means every process, so All is the only list that can be meant.
      setListOptions([ALL_LIST_OPTION]);
    }
    setFieldValue('listId', 'All');
  }, [values.processId]);

  // `hangupCause` me hamesha RAW value(s) jati hain, display label nahi.
  // Merged row (jaise SUCCESS) ke liye comma-separated list banti hai.
  const onDownload = async (hangupCause) => {
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {
        ...values,
        processId: values?.processId || 'All',
        listId: values?.listId || 'All'
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;
      payload['hangupCause'] = Array.isArray(hangupCause) ? hangupCause.join(',') : hangupCause;

      const response = await dispatch(downloadCallConnectivityReport(payload)).unwrap();

      if (response) {
        const fileName = `report_${formattedStartDate}_to_${formattedEndDate}.xlsx`;
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );

  const selectedLabel = selectedOption?.label || null;

  const { minDate, maxDate } = useMemo(() => {
    return getDateRangeFromLabel(selectedLabel, timezone);
  }, [selectedLabel, timezone]);

  // The archive options land after mount, so the month range is only known here.
  // The first search has to wait for it, otherwise it fetches today's range while
  // the filter already shows the whole month.
  useEffect(() => {
    if (minDate && maxDate) {
      setFieldValue('start_date', minDate);
      setFieldValue('end_date', maxDate);
      runInitialSearch({ start_date: minDate, end_date: maxDate });
    } else if (archiveOptions?.length > 0) {
      // Options loaded but the label gave no range - do not leave the first search pending.
      runInitialSearch();
    }
  }, [minDate?.getTime(), maxDate?.getTime(), archiveOptions?.length]);

  return (
    <div>
      <AdminHeader
        title="Call Connectivity Report"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isRefresh
        onRefreshClick={() => onSubmit()}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="start_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  minDate={minDate}
                  maxDate={values.end_date || maxDate}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="end_date"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.start_date || minDate}
                  maxDate={maxDate}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Process"
                  placeholder="Select Process"
                  value={values.processId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="processId"
                  options={processOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="List"
                  placeholder="Select List"
                  value={values.listId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="listId"
                  options={listOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.normalClearing?.length > 0 || reportData?.other?.length > 0 ? (
        <Card className="p-0 position-relative mb-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
            {reportData?.normalClearing?.length > 0 ? (
              <table className="report-list-table">
                <thead>
                  <tr className="title">
                    <th colSpan={2}>Connected</th>
                  </tr>
                  <tr>
                    <th>Disposition</th>
                    <th style={{ width: '90px' }} className="text-center">
                      Count
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.normalClearing?.map((val) => {
                    return (
                      <tr key={val?.label}>
                        <td>{val?.label}</td>
                        <td>
                          <div
                            className="text-center text-decoration-underline cursor-pointer"
                            onClick={() => onDownload(val?.rawCauses)}>
                            {val?.count}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td>
                      <strong>Total</strong>
                    </td>
                    <td>
                      <div
                        className="text-center text-decoration-underline cursor-pointer"
                        onClick={() => onDownload(collectRawCauses(reportData?.normalClearing))}>
                        {sumCounts(reportData?.normalClearing)}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : null}
            {reportData?.other?.length > 0 ? (
              <table className="report-list-table">
                <thead>
                  <tr className="title">
                    <th colSpan={2}>Not Connected</th>
                  </tr>
                  <tr>
                    <th>Disposition</th>
                    <th style={{ width: '90px' }} className="text-center">
                      Count
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.other?.map((val) => {
                    return (
                      <tr key={val?.label}>
                        <td>{val?.label}</td>
                        <td>
                          <div
                            className="text-center text-decoration-underline cursor-pointer"
                            onClick={() => onDownload(val?.rawCauses)}>
                            {val?.count}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td>
                      <strong>Total</strong>
                    </td>
                    <td>
                      <div
                        className="text-center text-decoration-underline cursor-pointer"
                        onClick={() => onDownload(collectRawCauses(reportData?.other))}>
                        {sumCounts(reportData?.other)}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : null}
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      <ProductReportDetails
        isOpen={!!infoDetails}
        onCloseClick={() => setInfoDetails(null)}
        infoDetails={infoDetails}
      />
    </div>
  );
};

export default CallConnectivityReport;
